import { useEffect } from 'react';
import { useLocation } from 'react-router';

import {
  recordSiteVisit,
  siteVisitPayload,
  type SiteVisitPayload,
} from '@/features/marketing/api/siteVisits';
import { isPlatformHost } from '@/features/storefront/storefrontHost';

/**
 * The paths already sent in this page load.
 *
 * Module state rather than a ref, because a ref dies with the component:
 * StrictMode mounts the layout twice, and going back to a page already seen is
 * not a second visit to it. A reload is, and a reload starts this module
 * afresh.
 */
const recorded = new Set<string>();

/**
 * How the visitor arrived: the referrer and campaign tags of the first page of
 * this load. Every later page in the same load carries them, so a visitor who
 * landed from a Google ad and went on to pricing is a Google-ad visit to
 * pricing, not a direct one - the referrer the browser keeps is the landing
 * page's anyway, and the tags are gone from the address after the first click.
 */
let arrival: SiteVisitPayload | undefined;

/** After the browser has painted and has nothing better to do. */
function whenIdle(task: () => void) {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(task, { timeout: 5000 });
  } else {
    window.setTimeout(task, 1);
  }
}

/** This page's visit, with the arrival's referrer and tags unless it has its own. */
function payloadFor(location: Pick<Location, 'pathname' | 'search'>): SiteVisitPayload {
  if (!arrival) {
    arrival = siteVisitPayload(location, document.referrer);
    delete arrival.path;
  }
  const own = siteVisitPayload(location, '');
  const tagged = Object.keys(own).some((key) => key.startsWith('utm_'));
  if (tagged) return { ...own, ...(arrival.referrer ? { referrer: arrival.referrer } : {}) };
  return { ...arrival, ...own };
}

/**
 * Count each marketing page the visitor opens, once per page load.
 *
 * Used by the marketing layout, so every page under it - home, pricing,
 * features, legal and the rest - is counted as the visitor reaches it, by a
 * full load or by an in-app link.
 *
 * Fire and forget. The page never waits for it and a visitor never hears about
 * it failing: a missed count is not worth a flicker, a toast or a console full
 * of red on the pages that have to sell the product.
 *
 * Only on NeonCarts' own hostnames. A merchant's custom domain serves this same
 * bundle, and its visitors are the merchant's to count, not ours.
 */
export function useTrackPageVisit() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    if (recorded.has(pathname) || !isPlatformHost()) return;
    recorded.add(pathname);
    const payload = payloadFor({ pathname, search });

    whenIdle(() => {
      try {
        recordSiteVisit(payload).catch(() => undefined);
      } catch {
        // Nothing to do: see above.
      }
    });
    // The search is read with the path it arrived with, not watched: a filter
    // changing the query string is the same page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
}
